import { useEffect, useState } from 'react';

/**
 * Installing Padhai as an app.
 *
 * Chrome on Android and desktop fires `beforeinstallprompt` once, early, and
 * only then can the page offer its own "Install" button. That can happen before
 * React has mounted anything, so this module listens from the moment it is
 * imported (main.tsx imports it first) and keeps the event for whoever asks.
 *
 * Safari on iPhone has no such event and no install button: the only way is
 * Share, then Add to Home Screen, so for it the app shows those words instead.
 */

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Keep the browser's own mini-bar from appearing; the app offers it instead.
    event.preventDefault();
    deferred = event as InstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

/** Already running as an installed app, on any platform. */
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari's own flag for a page opened from the home screen.
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/.test(ua) || (ua.includes('Mac') && navigator.maxTouchPoints > 1);
  // Chrome and Firefox on iOS cannot add to the home screen from their menu
  // the same way, so only Safari gets the instructions.
  return ios && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

export type InstallState =
  | { kind: 'installed' }
  | { kind: 'prompt'; install: () => Promise<boolean> }
  | { kind: 'ios' }
  | { kind: 'unavailable' };

export function useInstall(): InstallState {
  const [, setTick] = useState(0);
  useEffect(() => {
    const listener = () => setTick((tick) => tick + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  if (isStandalone()) return { kind: 'installed' };
  if (deferred) {
    const event = deferred;
    return {
      kind: 'prompt',
      install: async () => {
        await event.prompt();
        const { outcome } = await event.userChoice;
        // The event is spent either way; Chrome sends a fresh one later.
        deferred = null;
        notify();
        return outcome === 'accepted';
      },
    };
  }
  if (isIosSafari()) return { kind: 'ios' };
  return { kind: 'unavailable' };
}

/**
 * Registers the service worker in a production build. Development is left
 * alone: a worker caching Vite's dev server would fight hot reload.
 */
export function registerServiceWorker(): void {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // An app that cannot install still works as a website; nothing to tell.
    });
  });
}
